import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useHeader } from '../../../hooks/useHeader';
import classNames from 'classnames';

import ReorderOutlinedIcon from '@mui/icons-material/ReorderOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';

import styles from './Header.module.scss';
import logo from '../../../assets/images/Logo-bodhiwell.png';
import mundoIcon from '../../../assets/images/header/mundo.png';
import { NotificationDropdown } from '../../ui/NotificationDropdown/NotificationDropdown';
import EmailIcon from '@mui/icons-material/Email';
import Badge from '@mui/material/Badge';

import { useChat } from '../../../context/ChatContext';
import { MessagesDropdown } from './MessagesDropdown';
import '../../../assets/styles/official-signed-header.css';

export const Header = () => {
    const [imageError, setImageError] = useState(false);
    const navigate = useNavigate();
    const {
        user, isAuthenticated, t, i18n, isMenuOpen, isProfileMenuOpen, setIsProfileMenuOpen,
        isLanguageMenuOpen, setIsLanguageMenuOpen, isCommunityOpen, setIsCommunityOpen,
        isAtTop, profileMenuRef, languageMenuRef, communityMenuRef,
        toggleMenu, closeAllMenus, handleLogout, selectLanguage, handleScrollToSection
    } = useHeader();
    
    const { unreadCount, toggleChatDrawer, isChatDrawerOpen } = useChat();

    return (
        <>
            <header
                className={classNames(styles['header'], {
                    [styles['header--scrolled']]: !isAtTop,
                    [styles['header--is-menu-open']]: isMenuOpen,
                })}
                id="mainHeader"
            >
                <div
                    className={classNames(styles['header__overlay'], {
                        [styles['header__overlay--is-open']]: isMenuOpen
                    })}
                    onClick={toggleMenu}
                />
                <div className={styles['header-inner']}>

                    <Link to="/" className={styles['logo']} onClick={closeAllMenus} aria-label="Bodhiwell">
                        <img src={logo} alt="Bodhiwell" />
                    </Link>

                    <nav className={classNames(styles['nav'], {
                        [styles['nav--is-open']]: isMenuOpen
                    })} aria-label="Principal">
                        <div className={styles['nav-header']}>
                            <Link to="/" className={styles['logo']} onClick={closeAllMenus}>
                                <img src={logo} alt="Bodhiwell" />
                            </Link>
                            <button className={styles['nav-close']} onClick={toggleMenu}>
                                <CloseIcon sx={{ fontSize: 32 }} />
                            </button>
                        </div>

                        <div className={styles['nav-links']}>
                            <Link
                                to="/"
                                onClick={handleScrollToSection('about')}
                                className={styles['nav-link']}
                            >
                                {t('landing.header.about')}
                            </Link>
                            <Link
                                to="/"
                                onClick={handleScrollToSection('how-it-works')}
                                className={styles['nav-link']}
                            >
                                {t('landing.header.howItWorks')}
                            </Link>

                            <div className={styles['nav-dropdown']} ref={communityMenuRef}>
                                <button
                                    type="button"
                                    className={classNames(styles['nav-trigger'], {
                                        [styles['nav-trigger--active']]: isCommunityOpen
                                    })}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsCommunityOpen(prev => !prev);
                                    }}
                                >
                                    {t('landing.header.community')}
                                    <ExpandMoreIcon className={styles['arrow-icon']} />
                                </button>

                                <div className={classNames(styles['nav-menu'], { [styles['nav-menu--open']]: isCommunityOpen })} aria-label="Opciones de comunidad">

                                    <Link
                                        to="/professionals"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            closeAllMenus();
                                            setIsCommunityOpen(false);
                                        }}
                                        className={styles['nav-link']}
                                    >
                                        {t('landing.header.seeProfessionals', 'Ver profesionales')}
                                    </Link>

                                    <Link
                                        to="/profesiones-y-practicas"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            closeAllMenus();
                                            setIsCommunityOpen(false);
                                        }}
                                        className={styles['nav-link']}
                                    >
                                        {t('landing.header.professionsAndPractices', 'Profesiones y prácticas')}
                                    </Link>

                                    <Link
                                        to="/profesionales-landing"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            closeAllMenus();
                                            setIsCommunityOpen(false);
                                        }}
                                    >
                                        {t('landing.header.joinProfessional', 'Unirme como profesional')}
                                    </Link>
                                </div>
                            </div>

                            <a
                                href={process.env.REACT_APP_BLOG_URL}
                                target="_blank"
                                rel="noreferrer"
                                className={styles['nav-link']}
                            >
                                {t('landing.header.blog')}
                            </a>
                            <Link
                                to="/contact"
                                className={styles['nav-link']}
                                onClick={closeAllMenus}
                            >
                                {t('landing.header.contact')}
                            </Link>
                        </div>

                        <div className={styles['nav-footer']}>
                            <div className={styles['nav-mobile-lang']}>
                                <button
                                    onClick={() => selectLanguage('es')}
                                    className={classNames(styles['nav-lang-btn'], {
                                        [styles['nav-lang-btn--active']]: i18n.language === 'es'
                                    })}
                                >
                                    ES
                                </button>
                                <button
                                    onClick={() => selectLanguage('en')}
                                    className={classNames(styles['nav-lang-btn'], {
                                        [styles['nav-lang-btn--active']]: i18n.language === 'en'
                                    })}
                                >
                                    EN
                                </button>
                            </div>

                            {isAuthenticated ? (
                                <div className={styles['nav-mobile-user']}>
                                    <div className={styles['nav-user-info']}>
                                        {user?.details?.image && typeof user.details.image === 'string' && !imageError ? (
                                            <img 
                                                src={user.details.image.startsWith('http') ? user.details.image : `${process.env.REACT_APP_API_URL}${user.details.image}`} 
                                                alt={user.first_name || 'User'} 
                                                className={styles['nav-user-icon']}
                                                style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                                onError={() => setImageError(true)}
                                            />
                                        ) : (
                                            <AccountCircleIcon className={styles['nav-user-icon']} style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px', flexShrink: 0 }} />
                                        )}
                                        <div className={styles['nav-user-details']}>
                                            <span className={styles['nav-user-name']}>{user?.first_name} {user?.last_name}</span>
                                            <span className={styles['nav-user-email']}>{user?.email}</span>
                                        </div>
                                    </div>
                                    <div className={styles['nav-user-actions']}>
                                        <Link to="/dashboard" className={styles['nav-user-link']} onClick={closeAllMenus}>
                                            <HomeIcon /> {t('landing.header.myDashboard')}
                                        </Link>
                                        <Link to="/profile" className={styles['nav-user-link']} onClick={closeAllMenus}>
                                            <PersonIcon /> {t('landing.header.myProfile')}
                                        </Link>
                                        <button onClick={handleLogout} className={classNames(styles['nav-user-link'], styles['nav-logout'])}>
                                            {t('landing.header.logout')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles['nav-mobile-auth']}>
                                    <Link to="/login" className={styles['nav-login-btn']} onClick={closeAllMenus}>
                                        {t('landing.header.login', 'Acceder')}
                                    </Link>
                                    <Link to="/register" className={classNames(styles['btn'], styles['btn-outline'], styles['header-btn'], styles['btn-green'])} onClick={closeAllMenus}>
                                        {t('landing.header.register', 'Crear mi cuenta')}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </nav>

                    {/* 3. ACCIONES / BOTONES (DERECHA) */}
                    <div className={styles['actions']}>
                        {/* Selector de Idioma */}
                        <div className={styles['action-item']} ref={languageMenuRef}>
                            <button
                                className={classNames(styles['lang-switch'], {
                                    [styles['lang-switch--active']]: isLanguageMenuOpen
                                })}
                                type="button"
                                aria-label={`Idioma actual: ${i18n.language.toUpperCase()}`}
                                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                            >
                                <img src={mundoIcon} alt="" aria-hidden="true" />
                                <span>{i18n.language.toUpperCase()}</span>
                            </button>

                            {isLanguageMenuOpen && (
                                <div className={classNames(styles['dropdown'], styles['dropdown--lang'])}>
                                    <button
                                        onClick={() => selectLanguage('es')}
                                        className={classNames(styles['dropdown-item'], {
                                            [styles['dropdown-item--active']]: i18n.language === 'es'
                                        })}
                                    >
                                        <div className={styles['dropdown-icon-wrapper']}>
                                            <span className={styles['lang-abbr']}>ES</span>
                                        </div>
                                        <span>{t('landing.header.spanish')}</span>
                                    </button>

                                    <button
                                        onClick={() => selectLanguage('en')}
                                        className={classNames(styles['dropdown-item'], {
                                            [styles['dropdown-item--active']]: i18n.language === 'en'
                                        })}
                                    >
                                        <div className={styles['dropdown-icon-wrapper']}>
                                            <span className={styles['lang-abbr']}>EN</span>
                                        </div>
                                        <span>{t('landing.header.english')}</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Autenticación o Info de Usuario */}
                        {isAuthenticated ? (
                            <div className="bw-signed-actions">
                                <MessagesDropdown />
                                
                                <NotificationDropdown />

                                <div ref={profileMenuRef} style={{ position: 'relative', display: 'inline-block' }}>
                                    <button
                                        className="bw-header-user"
                                        onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                                    >
                                        {user?.details?.image && typeof user.details.image === 'string' && !imageError ? (
                                            <img 
                                                src={user.details.image.startsWith('http') ? user.details.image : `${process.env.REACT_APP_API_URL}${user.details.image}`} 
                                                alt={user.first_name || 'User'} 
                                                style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover' }}
                                                onError={() => setImageError(true)}
                                            />
                                        ) : (
                                            <AccountCircleIcon style={{ width: '30px', height: '30px', color: '#65756e' }} />
                                        )}
                                        <strong>{user?.first_name}</strong>
                                        <svg viewBox="0 0 16 16" aria-hidden="true" style={{ transform: isProfileMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                            <path d="m4 6 4 4 4-4"></path>
                                        </svg>
                                    </button>

                                    {isProfileMenuOpen && (
                                        <div className={styles['dropdown']}>
                                            <div className={styles['dropdown-header']}>
                                                <p className={styles['dropdown-user-name']}>{user?.first_name} {user?.last_name}</p>
                                                <p className={styles['dropdown-user-email']}>{user?.email}</p>
                                            </div>

                                            <div className={styles['dropdown-divider']} />

                                            <Link to="/dashboard" className={styles['dropdown-item']} onClick={closeAllMenus}>
                                                <div className={styles['dropdown-icon-wrapper']}>
                                                    <HomeIcon />
                                                </div>
                                                <span>{t('landing.header.myDashboard')}</span>
                                            </Link>

                                            <Link to="/profile" className={styles['dropdown-item']} onClick={closeAllMenus}>
                                                <div className={styles['dropdown-icon-wrapper']}>
                                                    <PersonIcon />
                                                </div>
                                                <span>{t('landing.header.myProfile')}</span>
                                            </Link>

                                            <div className={styles['dropdown-divider']} />

                                            <button onClick={handleLogout} className={classNames(styles['dropdown-item'], styles['dropdown-item--logout'])}>
                                                <span>{t('landing.header.logout')}</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <Link to="/login" className={styles['login-link']}>
                                    {t('landing.header.login', 'Acceder')}
                                </Link>
                                <Link to="/register" className={classNames(styles['btn'], styles['btn-outline'], styles['header-btn'], styles['btn-green'])}>
                                    {t('landing.header.register', 'Crear mi cuenta')}
                                </Link>
                            </>
                        )}
                    </div>

                    <button className={styles['toggler']} onClick={toggleMenu} aria-label={t('landing.header.menu', 'Menú')}>
                        <ReorderOutlinedIcon sx={{ fontSize: 32 }} />
                    </button>
                </div>
            </header>
        </>
    );
};